import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
} from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { readFile as xlsxRead, utils as xlsxUtils } from "xlsx";

import { uploadCurationMetadata } from "../../axios";

function CurationForm({ onFormSuccess, testMethodId, defaulValues }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [sampleFileData, setSampleFileData] = useState(null);
  const [fileData, setFileData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sampleJson, setSampleJson] = useState({});
  const [sampleOptions, setSampleOptions] = useState([]);
  const userInfo = useSelector((state) => state.userInfo);

  const selectedSample = Form.useWatch("sample", form);

  useEffect(() => {
    if (typeof selectedSample === "number") {
      form.setFieldsValue({ length: sampleJson?.[selectedSample]?.[defaulValues.lengthColHeader] });
      form.setFieldsValue({ width: sampleJson?.[selectedSample]?.[defaulValues.widthColHeader] });
      form.setFieldsValue({ thickness: sampleJson?.[selectedSample]?.[defaulValues.thicknessColHeader] });
    }
  }, [
    defaulValues.lengthColHeader,
    defaulValues.thicknessColHeader,
    defaulValues.widthColHeader,
    form,
    sampleJson,
    selectedSample,
  ]);

  useEffect(() => {
    form.setFieldsValue({
      upload: [],
    });
    return () => {
      setFileData(null);
    };
  }, [form]);

  const resetFormFields = () => {
    form.resetFields();
    setFileData(null);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    resetFormFields();
  };

  const handleOk = () => {
    setLoading(true);
    form
      .validateFields()
      .then((values) => {
        const formData = new FormData();

        if (fileData) {
          formData.append("file", fileData);
        }
        if (sampleFileData) {
          formData.append("file", sampleFileData);
        }

        Object.keys(values).forEach((key) => {
          if (![("upload", "upload-sample", "sample")].includes(key)) {
            formData.append(key, values[key]);
          }
        });
        formData.append("testMethod", testMethodId);
        formData.append("material_id", defaulValues.materialId);
        formData.append("sampleId", sampleJson?.[selectedSample]?.["Sample ID"]);
        formData.append("created_by", userInfo.name);

        dispatch(uploadCurationMetadata(formData))
          .then(() => {
            message.success("Metadata and file uploaded successfully!");
            onFormSuccess();
            setLoading(false);
            setIsModalVisible(false);
            resetFormFields();
          })
          .catch((error) => {
            console.error("Error uploading data:", error);
            message.error("Failed to upload data: "+ error);
            setLoading(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
        setLoading(false);
      });
  };

  const uploadSampleProps = {
    name: "file",
    multiple: false,
    accept: ".xlsx",

    beforeUpload: async (file) => {
      if (
        !(
          file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.name.endsWith(".xlsx")
        )
      ) {
        message.error(`${file.name} is not an Excel file`);
        return Upload.LIST_IGNORE;
      }
        setSampleFileData(null);
      const ab = await file.arrayBuffer();
      const wb = xlsxRead(ab);
      const ws = wb.Sheets[defaulValues.dataSheetName]; 
      const data = xlsxUtils.sheet_to_json(ws);

      setSampleJson(data);
      setSampleOptions(
        data.map((d, i) => {
          return { label: d["Sample ID"] + " (" + d["Sample Description"] + ")", value: i };
        })
      );
      setSampleFileData(file);

      form.setFieldsValue({ sample: 0 });
      const currentDesc = form.getFieldValue("description") || ""; 
      form.setFieldsValue({ description: `${currentDesc}\n[Sample File]: ${file.name}`.trim() });

      return false;
    },
    onChange: (info) => {
      let fileList = [...info.fileList];
      fileList = fileList.slice(-1);

      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
        setSampleFileData(info.file.originFileObj);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
      form.setFieldsValue({ "upload-sample": fileList });
    },
    onRemove: (file) => {
      setSampleFileData(null);
      form.setFieldsValue({ "upload-sample": [] });
      form.setFieldsValue({ sample: null });
      setSampleJson({});
      setSampleOptions([]);

      const currentDesc = form.getFieldValue("description") || "";
    const updatedDesc = currentDesc
      .split("\n")
      .filter((line) => !line.trim().includes("Sample File"))
      .join("\n")
      .trim();
      form.setFieldsValue({ description: updatedDesc });
    },
  };

  const uploadSpecimenProps = {
    name: "file",
    multiple: false,
    accept: ".csv",

    beforeUpload: (file) => {
      if (!(file.type === "text/csv" || file.name.endsWith(".csv"))) {
        message.error(`${file.name} is not a CSV file`);
        return Upload.LIST_IGNORE;
      }
      /* if (fileData) {
        message.error("Only one file can be uploaded at a time.");
        setFileData(file);
        return Upload.LIST_IGNORE;
      } */
      setFileData(file);
      const currentDesc = form.getFieldValue("description") || "";
      form.setFieldsValue({ description: `${currentDesc}\n[Specimen File]: ${file.name}`.trim() });
      return false;
    },
    onChange: (info) => {
      let fileList = [...info.fileList];
      fileList = fileList.slice(-1);

      if (info.file.status !== "uploading") {
        console.log(info.file, info.fileList);
      }
      if (info.file.status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
        setFileData(info.file.originFileObj);
      } else if (info.file.status === "error") {
        message.error(`${info.file.name} file upload failed.`);
      }
      form.setFieldsValue({ upload: fileList });
    },
    onRemove: (file) => {
      setFileData(null);
      form.setFieldsValue({ upload: [] });

      const currentDesc = form.getFieldValue("description") || "";
    const updatedDesc = currentDesc
      .split("\n")
      .filter((line) => !line.trim().includes("Specimen File"))
      .join("\n")
      .trim();
    form.setFieldsValue({ description: updatedDesc });
    },
  };

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  return (
    <>
      <Button type="ghost" size="small" style={{ width: 282 }} onClick={showModal}>
        <Space size={4}>
          <UploadOutlined /> <span>Upload Sample and Specimen</span>
        </Space>
      </Button>

      <Modal
        title={`${testMethodId} - Add Metadata and Upload Sample and Specimen Files`}
        open={isModalVisible}
        width="80%"
        onOk={handleOk}
        onCancel={handleCancel}
        okText="Upload"
        centered
      >
        <Spin spinning={loading}>
          <Form
            layout="vertical"
            form={form}
            initialValues={{
              name: defaulValues.materialName,
              description: defaulValues.materialDescription,
            }}
          >
            <Row gutter={24}>
              <Col span={8}>
                <Form.Item
                  name="name"
                  label={<div>Name</div>}
                  rules={[{ required: true, message: "Please input the Name" }]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[{ required: true, message: "Please input the Description" }]}
                >
                  <Input.TextArea rows={4} />
                </Form.Item>
                <Form.Item
                  name="sample"
                  label="Sample"
                  rules={[{ required: true, message: "Please select the Sample" }]}
                >
                  <Select placeholder="Select Sample" options={sampleOptions} />
                </Form.Item>
                <Form.Item
                  name="length"
                  label="Gauge Length"
                  rules={[
                    { required: true, message: "Please input the Gauge Length" },
                    {
                      validator: (_, value) =>
                        value < 0 ? Promise.reject(new Error("Gauge Length must be non-negative!")) : Promise.resolve(),
                      message: "Gauge Length must be non-negative!",
                    },
                  ]}
                >
                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} addonAfter="mm" />
                </Form.Item>
                <Form.Item
                  name="width"
                  label="Specimen Width"
                  rules={[
                    { required: true, message: "Please input the Specimen Width" },
                    {
                      validator: (_, value) =>
                        value < 0
                          ? Promise.reject(new Error("Specimen Width must be non-negative!"))
                          : Promise.resolve(),
                      message: "Specimen Width must be non-negative!",
                    },
                  ]}
                >
                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} addonAfter="mm" />
                </Form.Item>
                <Form.Item
                  name="thickness"
                  label="Specimen Thickness"
                  rules={[
                    { required: true, message: "Please input the Specimen Thickness" },
                    {
                      validator: (_, value) =>
                        value < 0
                          ? Promise.reject(new Error("Specimen Thickness must be non-negative!"))
                          : Promise.resolve(),
                      message: "Specimen Thickness must be non-negative!",
                    },
                  ]}
                >
                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} addonAfter="mm" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <div style={{ height: 500, backgroundColor: "#FFFFFF" }}>
                  <Form.Item
                    name="upload-sample"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[

                      {
                        required: true,
                        message: "Please upload only one XLSX File!",
                      },
                    ]}
                  >
                    <div
                      className="curation-upload"
                      style={{
                        height: 450,
                      }}
                    >
                      <Upload.Dragger
                      maxCount={1}
                      multiple={false}
                        name="files"
                        listType="picture"
                        {...uploadSampleProps}
                        style={{
                          backgroundColor: "#0b64c1",
                          border: "none !important;",
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined />
                        </p>
                        <Typography.Title level={5}>Upload Sample File</Typography.Title>
                        <Typography.Text strong>(Click Here or Drag and Drop File)</Typography.Text>
                        <br /><br />
                        <Typography.Text>Supported File Format: .xlsx</Typography.Text>
                      </Upload.Dragger>
                    </div>
                  </Form.Item>
                </div>
              </Col>
              <Col span={8}>
                <div style={{ height: 500, backgroundColor: "#FFFFFF" }}>
                  <Form.Item
                    name="upload"
                    valuePropName="fileList"
                    getValueFromEvent={normFile}
                    rules={[
                      {
                        required: true,
                        message: "Please upload only one CSV File!",
                      },
                    ]}
                  >
                    <div
                      className="curation-upload"
                      style={{
                        height: 450,
                      }}
                    >
                      <Upload.Dragger
                        name="files"
                        listType="picture"
                        multiple={false}        
                        maxCount={1}   
                        {...uploadSpecimenProps}
                        style={{
                          backgroundColor: "#06509e",
                          border: "none !important;",
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined />
                        </p>
                        <Typography.Title level={5}>Upload Specimen File</Typography.Title>
                        <Typography.Text strong>(Click Here or Drag and Drop File)</Typography.Text>
                        <br /><br />
                        <Typography.Text>Supported File Format: .csv</Typography.Text>
                      </Upload.Dragger>
                    </div>
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </>
  );
}

export default CurationForm;
